import { useId, useRef, useState } from "react";
import { Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import CircledBackButton from "@/features/Common/components/CircledBackButton";
import IconLoader from "@/features/Common/components/IconLoader";
import Panel from "@/features/Common/components/Panel";
import theme, {
  defaultFullscreenPageStyling,
  overrideDefaultLinkStyling,
} from "@/theme";
import { useNativeHealthSDK } from "@/hooks/useNativeHealthSDK";

/** Extracts the origin from a URL string, returning undefined for missing or malformed values. */
function getOrigin(url: string | undefined): string | undefined {
  if (!url) {
    return undefined;
  }
  try {
    return new URL(url).origin;
  } catch {
    return undefined;
  }
}

function VersionRow({ label, value }: { label: string; value: string }) {
  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      spacing={2}
    >
      <Typography variant="body2" sx={{ flexShrink: 0, whiteSpace: "nowrap" }}>
        {label}
      </Typography>
      <Typography
        variant="body2"
        fontWeight="bold"
        noWrap
        sx={{ textAlign: "right" }}
      >
        {value}
      </Typography>
    </Stack>
  );
}

export default function AboutApp() {
  const { t } = useTranslation();
  const appVersion = import.meta.env.VITE_APP_VERSION;
  const binahWebSdkVersion = import.meta.env.VITE_BINAH_WEB_SDK_VERSION;
  const appVersionID = useId();
  const { status } = useNativeHealthSDK();

  const envRows = [
    {
      key: "mainApi",
      origin: getOrigin(import.meta.env.VITE_APP_UNU_HEALTH_API_BASE_URI),
    },
    {
      key: "notificationsApi",
      origin: getOrigin(
        import.meta.env.VITE_APP_UNU_HEALTH_NOTIFICATIONS_API_BASE_URI
      ),
    },
    {
      key: "chronicCareApi",
      origin: getOrigin(
        import.meta.env.VITE_APP_UNU_HEALTH_CHRONIC_CARE_API_BASE_URI
      ),
    },
    {
      key: "logAQueryApi",
      origin: getOrigin(
        import.meta.env.VITE_APP_UNU_HEALTH_LOG_A_QUERY_ENDPOINT
      ),
    },
  ] as const;
  const hasAnyEnvRow = envRows.some((r) => r.origin);

  const clickCountRef = useRef(0);
  const [devMode, setDevMode] = useState(false);

  function onVersionPanelClick() {
    clickCountRef.current += 1;
    if (clickCountRef.current === 10) {
      setDevMode(true);
      clickCountRef.current = 0;
    }
  }

  return (
    <Stack spacing={3} sx={{ ...defaultFullscreenPageStyling }}>
      <CircledBackButton showLabel={true} />
      <Stack my={3}>
        <Typography variant="h2">{t("AboutApp.title")}</Typography>
      </Stack>

      <Panel>
        <Stack spacing={1} onClick={onVersionPanelClick}>
          <Typography
            id={appVersionID}
            variant="h5"
            color={theme.palette.neutral[500]}
          >
            {t("AboutApp.sections.version.label")}
          </Typography>
          <Typography aria-labelledby={appVersionID} fontWeight="bold">
            {appVersion}
          </Typography>
        </Stack>
      </Panel>

      {devMode && (
        <Panel>
          <Stack spacing={1}>
            <Typography variant="h5" color={theme.palette.neutral[500]}>
              {t("AboutApp.sections.devVersions.title")}
            </Typography>

            <VersionRow
              label={t("AboutApp.sections.devVersions.web")}
              value={appVersion}
            />
            {binahWebSdkVersion && (
              <VersionRow
                label={t("AboutApp.sections.devVersions.binahWeb")}
                value={binahWebSdkVersion}
              />
            )}
            {status?.nativeAppVersion && (
              <VersionRow
                label={t("AboutApp.sections.devVersions.nativeApp")}
                value={
                  status.nativeBuildNumber
                    ? `${status.nativeAppVersion} (${status.nativeBuildNumber})`
                    : status.nativeAppVersion
                }
              />
            )}
            {status?.binahNativeSdkVersion && (
              <VersionRow
                label={t("AboutApp.sections.devVersions.binahNative")}
                value={status.binahNativeSdkVersion}
              />
            )}
          </Stack>
        </Panel>
      )}

      {devMode && hasAnyEnvRow && (
        <Panel>
          <Stack spacing={1}>
            <Typography variant="h5" color={theme.palette.neutral[500]}>
              {t("AboutApp.sections.devEnvironment.title")}
            </Typography>

            {envRows.map(
              ({ key, origin }) =>
                origin && (
                  <VersionRow
                    key={key}
                    label={t(`AboutApp.sections.devEnvironment.${key}`)}
                    value={origin}
                  />
                )
            )}
          </Stack>
        </Panel>
      )}

      <Panel>
        <Stack spacing={1}>
          <Typography variant="h5" color={theme.palette.neutral[500]}>
            {t("AboutApp.sections.legalInformation.label")}
          </Typography>
          <Typography variant="body2">
            {t("AboutApp.sections.legalInformation.content")}
          </Typography>
        </Stack>
      </Panel>

      <Panel padding={0}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          p={2}
          sx={{ ...overrideDefaultLinkStyling }}
          component="a"
          href="/home/help/about-app/open-source-licenses"
        >
          <Typography variant="h4">
            {t("AboutApp.sections.openSourceLicenses.linkLabel")}
          </Typography>
          <IconLoader icon="ChevronNextIcon" />
        </Stack>
      </Panel>
    </Stack>
  );
}
